import {filter, isEmpty} from 'lodash';
import {useCallback, useContext, useEffect, useMemo, useState, Fragment} from 'react';
import {useTranslation} from 'react-i18next';

import {sessionHistoryApi} from 'api';
import {IbmMessageHistoryModal, InformationModal, Pagination, SessionHistoryNotesList, notifier} from 'components';
import {UserContext} from 'context';
import {useApiData, useObservable, useUserInfoSchema} from 'hooks';
import {Group, SessionHistory, SessionFilePipeline, SessionFile, SessionActivityName, SessionActivity} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {authDataStore} from 'services';
import {PaginatedData, PaginationOptions, UserSummary} from 'types';

import {REQUEST_SESSION_HISTORY_DATE_FORMAT} from '../constants';
import {ActivityFilterType} from '../enums';
import {ActivityFilters} from '../types';

import {ActivityMessagesModal} from './ActivityMessagesModal';
import {DownloadFiles} from './DownloadFiles';
import {DownloadReport} from './DownloadReport';
import {SessionAuditModal} from './SessionAuditModal';
import {FilePipelineDetails} from './SessionHistoryCell';
import {SessionHistoryGrid} from './SessionHistoryGrid';
import {SessionHistoryTable} from './SessionHistoryTable';
import {DownloadingFile} from './types';

const NOTES_MODAL_TITLE = TRANSLATION_KEYS.USER.ACTIVITY.SESSION_HISTORY.SESSION_HISTORY_TABLE.NOTES.TITLE;
const FILE_PIPELINE_DIALOG_TITLE = TRANSLATION_KEYS.USER.ACTIVITY.SESSION_HISTORY.FILE_PIPELINE_DETAILS_DIALOG.TITLE;

const defaultPagination: PaginationOptions = {
    page: 1,
    pages: 1,
    count: 0,
    pageSize: 10
};

const defaultSessionHistoryResponse: PaginatedData<SessionHistory> = {
    items: [],
    page: 1,
    pages: 1,
    count: 0
};

type AuditModalProps = {
    sessionId: string;
    open: boolean;
}

type ActivityModalProps = {
    open: boolean;
    session?: SessionHistory;
    activity?: SessionActivity;
}

type Props = {
    filters: ActivityFilters | null;
    users: UserSummary[];
    groups: Group[];
    isLoading: boolean;
    downloadFileDisabled?: boolean;
    setAvailableFilters: (filters: ActivityFilterType[]) => void;
    onToggleLoading: (value: boolean) => void;
};

export const SessionHistoryPage = ({
    filters, users, groups, isLoading, downloadFileDisabled, setAvailableFilters, onToggleLoading
}: Props) => {
    const {t} = useTranslation();
    const {settings} = useContext(UserContext);
    const {localUser} = useObservable(authDataStore);
    const hasAfterSessionFiles = useMemo(() => settings.featuresAvailability.afterSessionFiles, [settings]);
    const textChatHistoryEnabled = useMemo(() => settings.userSession?.textChatHistory?.enabled, [settings]);
    const avatarHistoryEnabled = useMemo(() => settings.userSession?.avatarHistory?.enabled, [settings]);
    const agentChatbotHistoryEnabled = useMemo(() => settings.userSession?.agentChatbotHistory?.enabled, [settings]);
    const audioTranscriptionEnabled = useMemo(() => settings?.userSession?.audioTranscription?.enabled, [settings]);
    const audioVideoTranscriptionEnabled = useMemo(() => settings.userSession?.audioVideoTranscription?.enabled, [settings]);

    const [pagination, setPagination] = useState<PaginationOptions>(defaultPagination);

    useUserInfoSchema({fetchOnInit: true, guestEnabled: settings.featuresAvailability.guestEnabled});

    const [notesModalProps, setNotesModalProps] = useState<{
        open: boolean;
        session?: SessionHistory;
    }>({open: false});

    const [auditModalProps, setAuditModalProps] = useState<AuditModalProps>({
        sessionId: null,
        open: false
    });

    const [activityMessagesModalProps, setActivityMessagesModalProps] = useState<ActivityModalProps>({open: false});
    const [ibmMessagesModalProps, setIbmMessagesModalProps] = useState<{open: boolean, sessionHistoryKey?: string}>({open: false});

    const [downloadingFiles, setDownloadingFiles] = useState<DownloadingFile[]>([]);
    const [selectedFilePipeline, setSelectedFilePipeline] = useState<SessionFilePipeline>(null);
    const [previewFile, setPreviewFile] = useState<{fileName: string, url: string} | null>(null);

    const abortController = useMemo(() => new AbortController(), []);

    const handleFilePreview = useCallback(async (sessionId: string, file: SessionFile) => {
        try {
            const url = await sessionHistoryApi.getExternalAfterSessionFilePreviewUrl(sessionId, file.name);
            setPreviewFile({fileName: file.name, url});
        } catch {
            notifier.error({details: t(TRANSLATION_KEYS.COMMON.MESSAGES.ERROR.UNEXPECTED)});
        }
    }, [t]);

    const handleFileDownload = useCallback((sessionId: string, file: SessionFile) => {
        const fileName = file.name;
        setDownloadingFiles((prev) => [...prev, {sessionId, fileName}]);

        const method = file.accessibleStorage ? sessionHistoryApi.downloadExternalAfterSessionFile :
            sessionHistoryApi.downloadAfterSessionFile;

        method(sessionId, fileName, {signal: abortController.signal}).catch(() => {
            notifier.error({details: t(TRANSLATION_KEYS.COMMON.MESSAGES.ERROR.UNEXPECTED)});
        }).finally(() => {
            const updatedDownloadingFiles = filter(downloadingFiles, (f) => f.sessionId !== sessionId && f.fileName !== fileName);
            setDownloadingFiles(updatedDownloadingFiles);
        });
    }, [downloadingFiles, abortController, setDownloadingFiles, t]);

    const loadSessionHistoryFn = useCallback(() => {
        if (!filters) {
            return Promise.resolve(defaultSessionHistoryResponse);
        }

        const _filters = {
            ...filters,
            startDate: filters.startDate.format(REQUEST_SESSION_HISTORY_DATE_FORMAT),
            endDate: filters.endDate.format(REQUEST_SESSION_HISTORY_DATE_FORMAT),
        };

        return sessionHistoryApi.getSessionHistory(_filters, {
            page: pagination.page,
            pageSize: pagination.pageSize
        });
    }, [filters, pagination.page, pagination.pageSize]);

    const {
        send: loadSessionHistory,
        data: sessionHistoryResponse,
        loading: sessionHistoryRequestInProgress
    } = useApiData(loadSessionHistoryFn);

    useEffect(() => () => {
        abortController.abort();
    }, [abortController]);

    useEffect(() => {
        if (isLoading !== sessionHistoryRequestInProgress) {
            onToggleLoading(sessionHistoryRequestInProgress);
        }
    }, [sessionHistoryRequestInProgress, isLoading, onToggleLoading]);

    const sessions: SessionHistory[] = useMemo(() => filter(sessionHistoryResponse?.items,
        (session) => session && !isEmpty(session.participants)),
    [sessionHistoryResponse]);

    useEffect(() => {
        if (sessionHistoryResponse) {
            const {pages, count} = sessionHistoryResponse;
            setPagination((prev) => ({
                ...prev,
                pages, count
            }));
        }
    }, [sessionHistoryResponse]);

    useEffect(() => {
        setAvailableFilters([
            ActivityFilterType.StartDate,
            ActivityFilterType.EndDate,
            ActivityFilterType.User,
            ActivityFilterType.Group,
            ActivityFilterType.Subgroup,
            ActivityFilterType.Result,
            ActivityFilterType.ResultInformation,
            ActivityFilterType.SessionId,
            ActivityFilterType.FilePipelineStatus,
            ActivityFilterType.ClientDevice,
            ActivityFilterType.UserInfoId
        ]);
    }, [setAvailableFilters]);

    const onOpenNotesModal = useCallback((session: SessionHistory) => {
        setNotesModalProps({session, open: true});
    }, []);

    const onCloseNotesModal = useCallback(() => {
        setNotesModalProps({open: false});
    }, []);

    const onOpenAuditModal = useCallback((sessionId: string) => {
        setAuditModalProps({sessionId, open: true});
    }, [setAuditModalProps]);

    const onCloseAuditModal = useCallback(() => {
        setAuditModalProps({sessionId: null, open: false});
    }, [setAuditModalProps]);

    const onOpenActivityMessagesModal = useCallback((session: SessionHistory, activity: SessionActivity) => {
        if (activity.name === SessionActivityName.TextChat && !textChatHistoryEnabled) {
            return;
        }
        setActivityMessagesModalProps({open: true, session, activity});
    }, [textChatHistoryEnabled]);

    const onCloseActivityDataModal = useCallback((params?: {ibm?: {sessionHistoryKey: string}}) => {
        setActivityMessagesModalProps({open: false});
        if (params?.ibm?.sessionHistoryKey) {
            setIbmMessagesModalProps({open: true, sessionHistoryKey: params.ibm.sessionHistoryKey});
        }
    }, []);

    const onCloseIbmMessagesModal = useCallback(() => {
        setIbmMessagesModalProps({open: false});
    }, []);

    const setPage = useCallback((value: number) => {
        setPagination((prev) => ({
            ...prev,
            page: value
        }));
    }, [setPagination]);

    const setPageSize = useCallback((value: number) => {
        setPagination((prev) => ({
            ...prev,
            pageSize: value
        }));
    }, [setPagination]);

    useEffect(() => {
        setPage(1);
    }, [setPage, filters, pagination.pageSize]);

    useEffect(() => {
        if (filters) {
            loadSessionHistory();
        }
    }, [loadSessionHistory, pagination.page, filters]);

    if (!filters) {
        return null;
    }

    return (
        <Fragment>
            {!isEmpty(sessions) &&
                <div className='tw-flex tw-flex-wrap tw-items-center'>
                    <DownloadReport
                        className='tw-my-2 tw-mr-2'
                        filters={filters}
                        pagination={pagination}
                        disabled={downloadFileDisabled}
                        isLoading={isLoading}
                    />
                    {hasAfterSessionFiles &&
                        <div className='tw-my-2 tw-mr-2'>
                            <DownloadFiles filters={filters} disabled={downloadFileDisabled || isLoading} />
                        </div>}
                    <Pagination
                        maxItemsToDisplay={9} // TODO: can we make "auto" on mobile and limit on PC?
                        className='tw-ml-auto tw-flex-1'
                        itemsClassName='tw-min-w-64 tw-justify-between sm:tw-justify-end'
                        rangeClassName='-sm:tw-hidden'
                        pageSelectorClassName='-sm:tw-hidden'
                        pageSize={pagination.pageSize}
                        pageNumber={pagination.page}
                        totalItems={pagination.count}
                        onPageNumberChange={setPage}
                        onPageSizeChange={setPageSize}
                    />
                </div>}
            <div className='tw-my-2'>
                <div className='-md:tw-hidden'>
                    <SessionHistoryTable
                        sessions={sessions}
                        users={users}
                        groups={groups}
                        downloadingFiles={downloadingFiles}
                        downloadFileDisabled={downloadFileDisabled}
                        isLoading={isLoading}
                        avatarHistoryEnabled={avatarHistoryEnabled}
                        agentChatbotHistoryEnabled={agentChatbotHistoryEnabled}
                        textChatHistoryEnabled={textChatHistoryEnabled}
                        audioTranscriptionEnabled={audioTranscriptionEnabled}
                        audioVideoTranscriptionEnabled={audioVideoTranscriptionEnabled}
                        onDownload={handleFileDownload}
                        onPreview={handleFilePreview}
                        onOpenAuditModal={onOpenAuditModal}
                        onOpenNotesModal={onOpenNotesModal}
                        onOpenActivityMessagesModal={onOpenActivityMessagesModal}
                        onSelectFilePipeline={setSelectedFilePipeline}
                    />
                </div>

                <div className='md:tw-hidden'>
                    <SessionHistoryGrid
                        sessions={sessions}
                        users={users}
                        groups={groups}
                        isLoading={isLoading}
                        textChatHistoryEnabled={textChatHistoryEnabled}
                        avatarHistoryEnabled={avatarHistoryEnabled}
                        agentChatbotHistoryEnabled={agentChatbotHistoryEnabled}
                        audioTranscriptionEnabled={audioTranscriptionEnabled}
                        audioVideoTranscriptionEnabled={audioVideoTranscriptionEnabled}
                        downloadingFiles={downloadingFiles}
                        downloadFileDisabled={downloadFileDisabled}
                        onDownload={handleFileDownload}
                        onPreview={handleFilePreview}
                        onOpenAuditModal={onOpenAuditModal}
                        onOpenNotesModal={onOpenNotesModal}
                        onOpenActivityMessagesModal={onOpenActivityMessagesModal}
                        onSelectFilePipeline={setSelectedFilePipeline}
                    />
                </div>

            </div>
            <SessionAuditModal {...auditModalProps} groups={groups} onClose={onCloseAuditModal} />
            <ActivityMessagesModal {...activityMessagesModalProps} onClose={onCloseActivityDataModal} />
            <IbmMessageHistoryModal {...ibmMessagesModalProps} onClose={onCloseIbmMessagesModal} />
            <InformationModal
                open={!!selectedFilePipeline}
                title={t(FILE_PIPELINE_DIALOG_TITLE)}
                onClose={() => setSelectedFilePipeline(null)}
            >
                <FilePipelineDetails pipeline={selectedFilePipeline} fullDetails />
            </InformationModal>
            <InformationModal title={t(NOTES_MODAL_TITLE)} {...notesModalProps} fullWidth maxWidth='sm' onClose={onCloseNotesModal}>
                <SessionHistoryNotesList session={notesModalProps.session} localUserEid={localUser.eid} />
            </InformationModal>
            <InformationModal
                open={!!previewFile}
                title={previewFile?.fileName}
                fullWidth
                maxWidth='lg'
                onClose={() => setPreviewFile(null)}
            >
                {previewFile &&
                    <video
                        key={previewFile.url}
                        src={previewFile.url}
                        controls
                        autoPlay
                        className='tw-w-full tw-max-h-[70vh] tw-bg-dark'
                    />}
            </InformationModal>
        </Fragment>
    );
};
